import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, TextField } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { AutovioCalendarEvent, isOtherEvent, isPendingInvitation } from "../../model/autovioCalendarEvents";
import { LoadingButton } from "@mui/lab";
import { useDeleteEventMutation } from "../../api/backoffice.api";
import { SlideUp } from "../SlideUp";
import { DialogCloseButton } from "../DialogCloseButton";
import { Form2Theme } from "../Form2";

export function DeleteCalendarEventDialog(props: {
  isOpen: boolean;
  calendarEvent: AutovioCalendarEvent;
  onClose: (appointmentDeleted: boolean) => void;
}) {
  const { isOpen, calendarEvent, onClose } = props;
  const deleteEventMutation = useDeleteEventMutation();
  const formProps = useForm<{ reason: string }>({ defaultValues: { reason: "" } });

  const deleteCalendarEvent = async () =>
    runSafely(
      async () =>
        deleteEventMutation.mutateAsync({
          calendarEventUid: calendarEvent.id,
          ...(!isOtherEvent(calendarEvent) && { reason: formProps.getValues().reason }),
        }),
      {
        errorMessage: `Fehler beim Löschen ${isPendingInvitation(calendarEvent) ? "der Einladung" : "des Termins"}`,
        successMessage: `${isPendingInvitation(calendarEvent) ? "Einladung" : "Termin"} erfolgreich gelöscht`,
        onSuccess: () => onClose(true),
      },
    );

  return (
    <FormProvider {...formProps}>
      <Dialog open={isOpen} onClose={() => onClose(false)} TransitionComponent={SlideUp}>
        <Form2Theme>
          <form onSubmit={formProps.handleSubmit(deleteCalendarEvent)}>
            <DialogTitle>{`${isPendingInvitation(calendarEvent) ? "Einladung" : "Termin"} löschen`}</DialogTitle>
            <DialogCloseButton onClick={() => onClose(false)} />
            <DialogContent>
              <Alert severity="warning">Achtung, Diese Aktion kann nicht rückgängig gemacht werden.</Alert>
              {!isOtherEvent(calendarEvent) && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <Controller
                    name="reason"
                    control={formProps.control}
                    rules={{
                      validate: (value) => {
                        const effectiveReason = (value ?? "").trim();
                        if (!effectiveReason) {
                          return "Bitte gib den Grund ein.";
                        } else if (effectiveReason.length < 5) {
                          return "Bitte formuliere den Grund etwas aussagekräftiger.";
                        } else {
                          return true;
                        }
                      },
                    }}
                    render={({ field, formState }) => {
                      const error = formState.errors.reason?.message;
                      return (
                        <TextField label="Grund" error={!!error} helperText={error} {...field} autoFocus required />
                      );
                    }}
                  />
                </FormControl>
              )}
            </DialogContent>
            <DialogActions>
              <LoadingButton
                variant="contained"
                loading={formProps.formState.isSubmitting}
                onClick={formProps.handleSubmit(deleteCalendarEvent)}
              >
                {`${isPendingInvitation(calendarEvent) ? "Einladung" : "Termin"} löschen`}
              </LoadingButton>
            </DialogActions>
          </form>
        </Form2Theme>
      </Dialog>
    </FormProvider>
  );
}
